import React from "react";
import { useTranslation } from "react-i18next";
import LegalDocumentView, { LegalSection } from "./LegalDocumentView";

type RichTextPart = string | { text: string; strong?: boolean; linkUrl?: string };

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  const sections: LegalSection[] = [
    {
      title: t("legal.privacy.sections.introduction.title"),
      paragraphs: t("legal.privacy.sections.introduction.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.purposes.title"),
      paragraphs: t("legal.privacy.sections.purposes.paragraphs", {
        returnObjects: true,
      }) as string[],
      bullets: t("legal.privacy.sections.purposes.bullets", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.types.title"),
      paragraphs: t("legal.privacy.sections.types.paragraphs", {
        returnObjects: true,
      }) as string[],
      bullets: t("legal.privacy.sections.types.bullets", {
        returnObjects: true,
      }) as string[],
      tailParagraphs: [
        t("legal.privacy.sections.types.followUp", {
          returnObjects: true,
        }) as RichTextPart[],
      ],
    },
    {
      title: t("legal.privacy.sections.legalBasis.title"),
      paragraphs: t("legal.privacy.sections.legalBasis.paragraphs", {
        returnObjects: true,
      }) as string[],
      bullets: t("legal.privacy.sections.legalBasis.bullets", {
        returnObjects: true,
      }) as Array<string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>>,
      tailParagraphs: [
        t("legal.privacy.sections.legalBasis.followUp", {
          returnObjects: true,
        }) as RichTextPart[],
      ],
    },
    {
      title: t("legal.privacy.sections.disclosure.title"),
      paragraphs: t("legal.privacy.sections.disclosure.paragraphs", {
        returnObjects: true,
      }) as string[],
      bullets: t("legal.privacy.sections.disclosure.bullets", {
        returnObjects: true,
      }) as Array<string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>>,
      tailParagraphs: [
        t("legal.privacy.sections.disclosure.followUp", {
          returnObjects: true,
        }) as RichTextPart[],
      ],
    },
    {
      title: t("legal.privacy.sections.retention.title"),
      paragraphs: t("legal.privacy.sections.retention.paragraphs", {
        returnObjects: true,
      }) as string[],
      bullets: t("legal.privacy.sections.retention.bullets", {
        returnObjects: true,
      }) as Array<string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>>,
    },
    {
      title: t("legal.privacy.sections.rights.title"),
      paragraphs: t("legal.privacy.sections.rights.paragraphs", {
        returnObjects: true,
      }) as string[],
      bullets: t("legal.privacy.sections.rights.bullets", {
        returnObjects: true,
      }) as Array<string | Array<string | { text: string; strong?: boolean; linkUrl?: string }>>,
      tailParagraphs: [
        t("legal.privacy.sections.rights.followUp", {
          returnObjects: true,
        }) as RichTextPart[],
      ],
    },
    {
      title: t("legal.privacy.sections.cookies.title"),
      paragraphs: t("legal.privacy.sections.cookies.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.children.title"),
      paragraphs: t("legal.privacy.sections.children.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.plugins.title"),
      paragraphs: t("legal.privacy.sections.plugins.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.links.title"),
      paragraphs: t("legal.privacy.sections.links.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.security.title"),
      paragraphs: t("legal.privacy.sections.security.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.changes.title"),
      paragraphs: t("legal.privacy.sections.changes.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
    {
      title: t("legal.privacy.sections.contact.title"),
      paragraphs: t("legal.privacy.sections.contact.paragraphs", {
        returnObjects: true,
      }) as string[],
    },
  ];

  return (
    <div className="space-y-6">
      <header className="border-b border-slate-200 pb-6">
        <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
          {t("legal.privacy.title")}
        </h2>
        <p className="mt-3 text-base font-medium text-blue-700">
          {t("legal.privacy.subtitle")}
        </p>
        <p className="mt-4 max-w-4xl text-base leading-8 text-slate-700 md:text-lg">
          {t("legal.privacy.description")}
        </p>
      </header>

      <LegalDocumentView sections={sections} />
    </div>
  );
};

export default PrivacyPolicy;
